import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from './config';
import { snip as snipAt } from './snip';

// Quick-jump search (UI polish batch, owner-approved 2026-07-30): a
// `/`-invoked overlay over the existing search endpoint — type, pick, go.
// Full-text search used to exist only inside Browse, two clicks deep under
// Explore; at ~5k claims there was no fast path to a known object. Rendered
// by Nav (the established home for global UI, same as the viewpoint
// drawer), so every surface gets it at once. No backend.

const TYPE_HINT = { claim: 'claim', text: 'page', link: 'link', tag: 'tag', user: 'person', source: 'source', question: 'question' };

const snip = (s, n = 90) => snipAt(s, n);

export default function QuickJump() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [sel, setSel] = useState(0);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // `/` opens it anywhere typing isn't already happening; Esc closes.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === '/' && !open) {
        const t = e.target;
        const typing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
        if (!typing) {
          e.preventDefault();
          setOpen(true);
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQ('');
      setResults([]);
      setSel(0);
      // the input mounts with the overlay in the same commit
      setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
    }
  }, [open]);

  // debounced search; a merge keyed on the latest query — late responses for
  // stale queries are dropped by comparing against the ref, not cancelled
  const latest = useRef('');
  useEffect(() => {
    latest.current = q;
    if (!open) return;
    const query = q.trim();
    if (!query) { setResults([]); setSel(0); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await apiFetch(`/api/objects/search?q=${encodeURIComponent(query)}`);
        if (!res.ok) throw new Error('search failed');
        const list = await res.json();
        if (latest.current === q) {
          setResults(list.slice(0, 12));
          setSel(0);
        }
      } catch {
        if (latest.current === q) setResults([]);
      } finally {
        if (latest.current === q) setSearching(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q, open]);

  const go = useCallback((obj) => {
    setOpen(false);
    navigate(`/object/${obj.id}`);
  }, [navigate]);

  const onInputKey = (e) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(s + 1, results.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); return; }
    if (e.key === 'Enter' && results[sel]) { go(results[sel]); }
  };

  if (!open) return null;

  return (
    <div
      onClick={() => setOpen(false)}
      style={{ position: 'fixed', inset: 0, background: 'color-mix(in srgb, var(--wv-bg) 62%, transparent)', zIndex: 95 }}
    >
      <div
        role="dialog"
        aria-label="Quick jump"
        onClick={(e) => e.stopPropagation()}
        className="wv-panel"
        style={{
          /* min(): below 588px the panel was flush edge-to-edge (auto margins
             collapse to 0 once maxWidth exceeds the viewport) */
          maxWidth: 'min(560px, calc(100vw - 2 * var(--wv-sp-6)))',
          boxSizing: 'border-box',
          margin: '12vh auto 0',
          padding: 'var(--wv-sp-4)',
          boxShadow: '0 16px 48px var(--wv-shadow)',
        }}
      >
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onInputKey}
          placeholder="Jump to anything — type to search"
          aria-label="Search objects"
          style={{ width: '100%', boxSizing: 'border-box', fontSize: 'var(--wv-fs-lg)', padding: 'var(--wv-sp-3) var(--wv-sp-4)' }}
        />
        {q.trim() && (
          <div style={{ marginTop: 'var(--wv-sp-3)', maxHeight: '52vh', overflowY: 'auto' }}>
            {results.map((r, i) => (
              <div
                key={r.id}
                onClick={() => go(r)}
                onMouseEnter={() => setSel(i)}
                className={i === sel ? 'wv-dropdown-item wv-tint' : 'wv-dropdown-item'}
                style={{ borderRadius: 'var(--wv-r-sm)', border: 0, display: 'flex', gap: 'var(--wv-sp-3)', alignItems: 'baseline' }}
              >
                <span style={{ flex: 1, minWidth: 0 }}>{snip(r.content)}</span>
                <span className="wv-muted" style={{ fontSize: 'var(--wv-fs-xs)', flexShrink: 0 }}>
                  {TYPE_HINT[r.object_type] || r.object_type}
                </span>
              </div>
            ))}
            {!searching && results.length === 0 && (
              <div className="wv-muted" style={{ fontSize: 'var(--wv-fs-md)', padding: 'var(--wv-sp-2) var(--wv-sp-2)' }}>
                Nothing matches.
              </div>
            )}
          </div>
        )}
        <div className="wv-muted" style={{ fontSize: 'var(--wv-fs-xs)', marginTop: 'var(--wv-sp-3)' }}>
          ↑↓ to choose · Enter to open · Esc to close
        </div>
      </div>
    </div>
  );
}
